import React, {
  useState,
  RefObject,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import DatePicker from 'react-multi-date-picker';

import { CalendarView, validateDateRange } from '../helper';
import { Ref as DateRangeCalendarRef } from './DateRangeCalendar';
import { Ref as DataGranularitiesRef } from './DataGranularities';
import { Ref as PreDefinedCalendarFiltersRef } from '../components/PreDefinedCalendarFilters';

import '../styles/dateRangePicker.orange.css';

export interface Ref {
  selectedDates: Date[];
  handleCalendarView: (calendarView: CalendarView) => void;
  handleChangeFromAndToDates: (dateFrom: Date, dateTo: Date) => void;
}

interface Props {
  dateTo?: Date;
  dateFrom?: Date;
  dateRangeCalendarRef: RefObject<DateRangeCalendarRef>;
  dataGranularitiesRef: RefObject<DataGranularitiesRef>;
  preDefinedCalendarFiltersRef: RefObject<PreDefinedCalendarFiltersRef>;
}

const DateRangePicker: ForwardRefRenderFunction<Ref, Props> = (
  {
    dateTo = new Date(),
    dateRangeCalendarRef,
    dataGranularitiesRef,
    dateFrom = new Date(),
    preDefinedCalendarFiltersRef,
  },
  ref
) => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([
    dateFrom,
    dateTo,
  ]);
  const [calendarView, setCalendarView] = useState<CalendarView>(
    CalendarView.DAY
  );

  const handleChangeFromAndToDates = (dateFrom: Date, dateTo: Date) => {
    const maxDays = dataGranularitiesRef.current?.selectedGranularity?.maxDays;
    const dateRange = validateDateRange(dateFrom, dateTo, maxDays);

    setSelectedDates([dateRange.dateFrom, dateRange.dateTo]);
  };

  const handleCalendarView = (calendarView: CalendarView) =>
    setCalendarView(calendarView);

  const handleChange = (
    date: any,
    options: {
      validatedValue: string | Array<string>;
      input: HTMLElement;
      isTyping: boolean;
    }
  ) => {
    const parsedDates = Array.isArray(options.validatedValue)
      ? options.validatedValue.map((el) => new Date(el))
      : [];

    handleChangeFromAndToDates(parsedDates[0], parsedDates[1]);

    dateRangeCalendarRef.current?.handleChangeFromAndToDates(
      parsedDates[0],
      parsedDates[1]
    );
    preDefinedCalendarFiltersRef.current?.handleSelectedOptions(undefined);
    preDefinedCalendarFiltersRef.current?.handleShowCustomOption(false);
    preDefinedCalendarFiltersRef.current?.preDefinedCalendarCustomFilterRef.current?.handleChangeEnteredValues(
      { daysFrom: 0, daysTo: 0 }
    );
  };

  useImperativeHandle(ref, () => ({
    selectedDates,
    handleCalendarView,
    handleChangeFromAndToDates,
  }));

  return (
    <DatePicker
      className="orange"
      range
      rangeHover
      value={selectedDates}
      containerStyle={{
        width: '100%',
      }}
      onChange={handleChange}
      dateSeparator="      to      "
      numberOfMonths={
        calendarView === CalendarView.MONTH ||
        calendarView === CalendarView.YEAR
          ? 1
          : 2
      }
      onlyMonthPicker={calendarView === CalendarView.MONTH}
      onlyYearPicker={calendarView === CalendarView.YEAR}
    />
  );
};

export default forwardRef<Ref, Props>(DateRangePicker);
